import { useRef, useState, useEffect } from "react";
import { Camera, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui";
import { ACCEPTED_MIME, MAX_PHOTO_BYTES } from "./assetPhotoUpload";

// =============================================================================
// PhotoCaptureField — single-photo camera capture embedded in create forms.
// Mobile-first: capture="environment" hints phones to open the rear camera.
// Parent holds the File; upload happens inside the create mutation after the
// asset ID is known (see assetPhotoUpload.uploadAssetPhoto). T-051 Part B.
// =============================================================================

interface Props {
  value:    File | null;
  onChange: (file: File | null) => void;
  label?:   string;
  hint?:    string;
}

export function PhotoCaptureField({
  value, onChange,
  label = "Photo",
  hint = "Quick reference shot — snap it now, manage more from the detail page later.",
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Build + tear down object URL for preview
  useEffect(() => {
    if (!value) { setPreview(null); return; }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPTED_MIME.includes(f.type)) {
      setErr("Unsupported image type. JPEG / PNG / WEBP / HEIC only.");
      return;
    }
    if (f.size > MAX_PHOTO_BYTES) {
      setErr(`Photo too large (${(f.size / 1024 / 1024).toFixed(1)} MB, max 10 MB).`);
      return;
    }
    setErr(null);
    onChange(f);
  };

  const clear = () => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <div className="text-[12px] text-text-muted mb-1">{label}</div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_MIME.join(",")}
        capture="environment"
        onChange={onPick}
        className="hidden"
      />
      {value && preview ? (
        <div className="relative inline-block">
          <img
            src={preview}
            alt="Captured"
            className="max-w-full max-h-48 rounded-md border border-line object-cover"
          />
          <button
            type="button"
            onClick={clear}
            className="absolute top-1 right-1 p-1 bg-black/70 hover:bg-black/90 text-white rounded-full"
            aria-label="Remove photo"
          >
            <X size={14} />
          </button>
          <div className="mt-1 text-[11px] text-text-muted font-mono truncate max-w-[16rem]">
            {value.name} · {(value.size / 1024).toFixed(0)} KB
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="secondary"
          icon={Camera}
          onClick={() => inputRef.current?.click()}
        >
          Take photo
        </Button>
      )}
      {hint && !value && (
        <div className="text-[11px] text-text-muted mt-1">{hint}</div>
      )}
      {err && (
        <div className="mt-1 text-[12px] text-red-400 flex items-center gap-1">
          <AlertTriangle size={12} /> {err}
        </div>
      )}
    </div>
  );
}
